import { useEffect, useCallback, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import styled, { ThemeProvider } from "styled-components";
import { itemOrderChange, undo, redo, dayChange } from "../actions";
import { getCurrentDay } from "../state/currentDay.selectors";
import { h } from "../h";
import { TopMenu } from "./TopMenu";
import { Main } from "./Main";
import { theme } from "../theme";
import { getTodayDate } from "../utils/date";

const AppStyled = styled.div`
    font-size: ${({ theme }) => theme.fontM};
    color: ${({ theme }) => theme.colorMain};
`;

export const App = (): ReactElement => {
    const dispatch = useDispatch();
    const currentDay = useSelector(getCurrentDay);
    useEffect(() => {
        dispatch(dayChange(getTodayDate()));
    }, [dispatch]);
    useEffect(() => {
        const globalKeyDownHandler = (event: KeyboardEvent): void => {
            if (event.ctrlKey && event.key.toLowerCase() === `z`) {
                event.preventDefault();
                event.stopImmediatePropagation();
                if (event.shiftKey) {
                    dispatch(redo({ date: currentDay }));
                } else {
                    dispatch(undo({ date: currentDay }));
                }
            }
        };
        document.addEventListener(`keydown`, globalKeyDownHandler);
        return () => {
            document.removeEventListener(`keydown`, globalKeyDownHandler);
        };
    }, [dispatch, currentDay]);
    const onDragEnd = useCallback(
        ({ source, destination }: DropResult) => {
            if (!destination) {
                return;
            }
            if (source.index === destination.index) {
                return;
            }
            dispatch(
                itemOrderChange({
                    date: currentDay,
                    from: source.index,
                    to: destination.index,
                }),
            );
        },
        [dispatch, currentDay],
    );
    return h(
        DragDropContext,
        { onDragEnd },
        h(
            ThemeProvider,
            {
                theme,
            },
            h(AppStyled, {}, h(TopMenu), h(Main)),
        ),
    );
};
